import type {VehicleSafelistingsAccountability} from "@/src/modules/vehicles/types";

export type LimitBannerVariant = "info" | "warning" | "destructive";

function permitsAvailabilityTitle(remaining: number, totalAllowed: number) {
  return `You have ${remaining} of ${totalAllowed} permits available.`;
}

export type LimitBannerDescriptor = {
  variant: LimitBannerVariant;
  title: string;
  shortfall: number | null;
  remainingAfterSubmit: number | null;
  totalAllowed: number;
  used: number;
  remaining: number;
  totalDays: number;
  nextReset: string | null;
  rollingDays: number | null;
  periodType: string | null;
  /** Server message from `/safelistings/availability` when selection is blocked. */
  availabilityReason?: string | null;
};

export function computeLimitBanner(
  accountability: VehicleSafelistingsAccountability | null,
  totalDays: number,
): LimitBannerDescriptor | null {
  if (!accountability) return null;
  const {total_allowed, used, remaining, next_reset, rolling_days, period_type} = accountability;
  if (total_allowed == null || used == null || remaining == null) return null;

  const safeTotalDays = Math.max(0, totalDays);
  const base = {
    totalAllowed: total_allowed,
    used,
    remaining,
    totalDays: safeTotalDays,
    nextReset: next_reset,
    rollingDays: rolling_days,
    periodType: period_type,
  };

  if (remaining === 0) {
    return {
      ...base,
      variant: "destructive",
      title: permitsAvailabilityTitle(remaining, total_allowed),
      shortfall: safeTotalDays > 0 ? safeTotalDays : null,
      remainingAfterSubmit: 0,
    };
  }

  if (safeTotalDays > remaining) {
    return {
      ...base,
      variant: "warning",
      title: permitsAvailabilityTitle(remaining, total_allowed),
      shortfall: safeTotalDays - remaining,
      remainingAfterSubmit: null,
    };
  }

  return {
    ...base,
    variant: "info",
    title: permitsAvailabilityTitle(remaining, total_allowed),
    shortfall: null,
    remainingAfterSubmit: remaining - safeTotalDays,
  };
}
